import React, { useState } from 'react';
import {
  Button,
  Space,
  Tag,
  Modal,
  Row,
  Col,
  Typography,
  Descriptions,
  Divider,
  Table,
  message,
} from 'antd';
import {
  FilePdfOutlined,
  FileTextOutlined,
  ToolOutlined,
  BgColorsOutlined,
  SnippetsOutlined,
  OrderedListOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import MyTable from '../helpers/MyTable';
import SubPageLayout from '../helpers/SubPageLayout';
import SubPageHeader from '../helpers/SubPageHeader';

const { Title, Text } = Typography;

// Demo products currently in production
const productionProducts = [
  {
    id: 1,
    productCode: 'MineSAFE_Modular_Design',
    productName: 'MineSAFE Modular Design',
    serialNumber: 'MAF1236',
    image: '/product_images/MineSAFE_Modular_Design.png',
    companyName: 'Assmang',
    siteName: 'Black Rock',
    country: 'South Africa',
    industry: 'MineSAFE',
    mafqNumber: 'MAQ1001',
    mafNumber: 'MAF1236',
    dueDate: '2025-06-01',
    businessManager: 'Duane Mac Farlane',
    fabricator: 'MineARC Fabrication',
    paintBlast: 'SurfaceCoat Solutions',
    currentStage: 'Fabrication',
  },
  {
    id: 2,
    productCode: 'ChemSAFE_Standard_Design',
    productName: 'ChemSAFE Standard Design',
    serialNumber: 'MAF2231',
    image: '/product_images/ChemSAFE_Standard_Design.png',
    companyName: 'SafeChem Industries',
    siteName: 'Plant 7 Expansion',
    country: 'South Africa',
    industry: 'ChemSAFE',
    mafqNumber: 'MAQ1002',
    mafNumber: 'MAF2231',
    dueDate: '2025-07-15',
    businessManager: 'Sarah Johnson',
    fabricator: 'SteelWorks Pty Ltd',
    paintBlast: 'PrimeBlast Services',
    currentStage: 'Paint & Blast',
  },
  {
    id: 3,
    productCode: 'TunnelSAFE_Standard_Design',
    productName: 'TunnelSAFE Standard Design',
    serialNumber: 'MAF3312',
    image: '/product_images/TunnelSAFE_Standard_Design.png',
    companyName: 'Global Tunneling Co.',
    siteName: 'Metro Tunnel Phase 2',
    country: 'Australia',
    industry: 'TunnelSAFE',
    mafqNumber: 'MAQ1003',
    mafNumber: 'MAF3312',
    dueDate: '2025-05-10',
    businessManager: 'Michael Chen',
    fabricator: 'MineARC Fabrication',
    paintBlast: 'SurfaceCoat Solutions',
    currentStage: 'Build (Pre-Electrical)',
  },
];

const stageColors = {
  'Pre-build': 'default',
  Fabrication: 'blue',
  'Paint & Blast': 'purple',
  Materials: 'gold',
  Stickers: 'cyan',
  'Build (Pre-Electrical)': 'lime',
  'Electrician Testing': 'orange',
  'Build (Post-Electrical)': 'geekblue',
  Approvals: 'green',
  Shipping: 'volcano',
};

// ---- Build Sheet structure (all stages & tasks) ----
const BUILD_SHEET_STRUCTURE = [
  {
    name: 'Pre-build',
    tasks: [
      'Pre-build sign offs',
      'Build Sheet',
      'Drawings',
      'Fabrication sheet',
      'Paint Sheet',
      'Send to Fabrication',
    ],
  },
  {
    name: 'Fabrication',
    tasks: ['Fabrication inspection', 'Fabrication Sign off'],
  },
  {
    name: 'Paint & Blast',
    tasks: ['Paint Sign off'],
  },
  {
    name: 'Materials',
    tasks: ['Bill of Materials'],
  },
  {
    name: 'Stickers',
    tasks: ['Print stickers'],
  },
  {
    name: 'Build (Pre-Electrical)',
    sections: [
      {
        title: 'Front Section / First Fix wiring',
        tasks: [
          'Ducting',
          'Fit all glands',
          'First-fix wiring, including GuardIAN cables',
          'Front wall and door stripe to be applied',
          'Door seal installation',
          'Port-hole window',
          '2 inch Vent valves',
          'Strobe lights and siren',
        ],
      },
      {
        title: 'Back Section',
        tasks: [
          'Back wall',
          'Comms box',
          'Iso switch',
          'Air-conditioning condenser external and evaporator install internal (Vac test and Gas release)',
          'Battery box fitout',
          'Cable looms',
          'Probes',
          'Floor mat',
          'Battery box doors fit-out',
        ],
      },
      {
        title: 'CAMS Installation',
        tasks: [
          'CAMS external install',
          'CAMS internal install (including ECU and ECU wiring)',
        ],
      },
      {
        title: 'Scrubber System',
        tasks: ['Install scrubber', 'Connect low-voltage wiring'],
      },
    ],
  },
  {
    name: 'Electrician Testing',
    sections: [
      {
        title: 'Electrical Work',
        tasks: [
          'Electrician to complete all electrical works on the chamber',
          'Produce electrical certificate',
        ],
      },
    ],
  },
  {
    name: 'Build (Post-Electrical)',
    sections: [
      {
        title: 'Interior Fit-Out',
        tasks: [
          'Seats',
          'Floor mat',
          'Toilet curtain',
          'Gas cylinder brackets',
          'Other internal accessories',
        ],
      },
      {
        title: 'External / Internal Finishing',
        tasks: [
          'Operational stickers',
          'External green stripe',
          'Final external and internal markings',
        ],
      },
      {
        title: 'Commissioning',
        tasks: [
          'Load chemicals and consumables',
          'Clean and tidy chamber',
          'Set parameters on scrubber',
          'Carry out vacuum test on chamber',
        ],
      },
    ],
  },
  {
    name: 'Approvals',
    tasks: [
      'Approval Check list',
      'Two Supervisors from workshop',
      'Sales BDM sign off',
    ],
  },
  {
    name: 'Shipping',
    tasks: ['Despatch', 'Shipping photos'],
  },
];

// Simple demo BOM data
const DEMO_BOM = [
  {
    key: 1,
    itemNo: '50000 10-1/2',
    description: 'Male Adaptor 10mm x 1/2',
    quantity: 1,
    location: 'NORTHLND',
  },
  {
    key: 2,
    itemNo: 'ACCESSORYBOX.',
    description: 'Accessory Box for Chamber',
    quantity: 1,
    location: 'NORTHLND',
  },
  {
    key: 3,
    itemNo: 'BATTERY',
    description: 'Battery 12V, 220AH (ST12200)',
    quantity: 20,
    location: 'NORTHLND',
  },
];

const stickerTypes = [
  'Product Name stickers',
  'Gas Sensors table stickers',
  'Scrubber stickers',
  'External stickers',
  'Indicators stickers',
];

const ProductionTimeline = () => {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeModal, setActiveModal] = useState(null); // 'buildSheet' | 'fabrication' | 'paint' | 'stickers' | 'bom'
  const [selectedProduct, setSelectedProduct] = useState(null);

  const openModal = (type, product) => {
    setSelectedProduct(product);
    setActiveModal(type);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedProduct(null);
  };

  const handleViewClick = (product) => {
    history.push('/production-timeline/view', { product });
  };

  const handleDownloadPdf = (label) => {
    // Placeholder – wire up to real PDF generator later
    message.info(`Downloading ${label} as PDF… (to be implemented)`);
  };

  const actionBtnStyle = {
    height: 28,
    borderRadius: 999,
    padding: '0 10px',
    fontSize: 11,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  const columns = [
    {
      title: '',
      dataIndex: 'image',
      key: 'image',
      width: 90,
      render: (src, record) => (
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: 12,
            overflow: 'hidden',
            background:
              'radial-gradient(circle at 0% 0%, #e5f0ff, #eff6ff 45%, #f9fafb)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={src}
            alt={record.productName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>
      ),
    },
    {
      title: 'Product Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Current Stage',
      dataIndex: 'currentStage',
      key: 'currentStage',
      render: (stage) => (
        <Tag color={stageColors[stage] || 'default'}>{stage}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size={4} wrap>
          <Button
            style={actionBtnStyle}
            type="text"
            onClick={() => openModal('buildSheet', record)}
          >
            <FileTextOutlined />
            Build Sheet
          </Button>
          <Button
            style={actionBtnStyle}
            type="text"
            onClick={() => openModal('fabrication', record)}
          >
            <ToolOutlined />
            Fabrication
          </Button>
          <Button
            style={actionBtnStyle}
            type="text"
            onClick={() => openModal('paint', record)}
          >
            <BgColorsOutlined />
            Paint
          </Button>
          <Button
            style={actionBtnStyle}
            type="text"
            onClick={() => openModal('stickers', record)}
          >
            <SnippetsOutlined />
            Stickers
          </Button>
          <Button
            style={actionBtnStyle}
            type="text"
            onClick={() => openModal('bom', record)}
          >
            <OrderedListOutlined />
            BOM
          </Button>
          <Button
            style={{
              ...actionBtnStyle,
              type: 'primary',
            }}
            type="primary"
            onClick={() => handleViewClick(record)}
          >
            <EyeOutlined />
            View
          </Button>
        </Space>
      ),
    },
  ];

  const filteredProducts = productionProducts.filter((p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (p.serialNumber || '').toLowerCase().includes(q) ||
      (p.productName || '').toLowerCase().includes(q) ||
      (p.productCode || '').toLowerCase().includes(q)
    );
  });

  return (
    <SubPageLayout title="Production Timeline">
      <h3 style={{ marginBottom: 8 }}>
        Displaying all products currently in the build process
      </h3>

      <SubPageHeader
        searchLoading={false}
        searchPlaceholder="Search products by serial, name or code"
        onSearch={(value) => setSearchQuery(value)}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <MyTable columns={columns} dataSource={filteredProducts} rowKey="id" />

      {/* ---------- BUILD SHEET MODAL ---------- */}
      <Modal
        visible={activeModal === 'buildSheet'}
        onCancel={closeModal}
        footer={null}
        width={1000}
        centered
        destroyOnClose
        title={null}
      >
        {selectedProduct && (
          <div
            style={{
              padding: 24,
              borderRadius: 16,
              background:
                'linear-gradient(135deg, #ffffff 0%, #f3f4ff 35%, #eef7ff 100%)',
            }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Build Sheet – {selectedProduct.productName}
                </Title>
                <Text type="secondary">
                  Serial {selectedProduct.serialNumber} · MAFQ{' '}
                  {selectedProduct.mafqNumber}
                </Text>
              </Col>
              <Col>
                <Button
                  icon={<FilePdfOutlined />}
                  type="primary"
                  onClick={() =>
                    handleDownloadPdf('Build Sheet for this product')
                  }
                >
                  Download as PDF
                </Button>
              </Col>
            </Row>

            <Divider />

            <Row gutter={24}>
              <Col span={10}>
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Customer">
                    {selectedProduct.companyName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Site">
                    {selectedProduct.siteName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Country">
                    {selectedProduct.country}
                  </Descriptions.Item>
                  <Descriptions.Item label="Business Manager">
                    {selectedProduct.businessManager}
                  </Descriptions.Item>
                  <Descriptions.Item label="Fabricator">
                    {selectedProduct.fabricator}
                  </Descriptions.Item>
                  <Descriptions.Item label="Paint & Blast">
                    {selectedProduct.paintBlast}
                  </Descriptions.Item>
                  <Descriptions.Item label="Due Date">
                    {selectedProduct.dueDate}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={14}>
                <div
                  style={{
                    maxHeight: 420,
                    overflowY: 'auto',
                    paddingRight: 8,
                  }}
                >
                  {BUILD_SHEET_STRUCTURE.map((stage, index) => (
                    <div
                      key={stage.name}
                      style={{
                        marginBottom: 16,
                        paddingBottom: 8,
                        borderBottom:
                          index === BUILD_SHEET_STRUCTURE.length - 1
                            ? 'none'
                            : '1px dashed #e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          marginBottom: 4,
                        }}
                      >
                        {index + 1}. {stage.name}
                      </div>

                      {stage.tasks && (
                        <ul
                          style={{
                            paddingLeft: 20,
                            marginBottom: 0,
                            fontSize: 13,
                          }}
                        >
                          {stage.tasks.map((task) => (
                            <li key={task}>{task}</li>
                          ))}
                        </ul>
                      )}

                      {stage.sections &&
                        stage.sections.map((section) => (
                          <div key={section.title} style={{ marginTop: 6 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                marginBottom: 2,
                              }}
                            >
                              {section.title}
                            </div>
                            <ul
                              style={{
                                paddingLeft: 20,
                                marginBottom: 0,
                                fontSize: 13,
                              }}
                            >
                              {section.tasks.map((task) => (
                                <li key={task}>{task}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* ---------- FABRICATION MODAL ---------- */}
      <Modal
        visible={activeModal === 'fabrication'}
        onCancel={closeModal}
        footer={null}
        width={1000}
        centered
        destroyOnClose
        title={null}
      >
        {selectedProduct && (
          <div
            style={{
              padding: 24,
              borderRadius: 16,
              background: '#ffffff',
            }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Fabrication Check List – {selectedProduct.productName}
                </Title>
                <Text type="secondary">
                  Fabricator: {selectedProduct.fabricator} · Serial{' '}
                  {selectedProduct.serialNumber}
                </Text>
              </Col>
              <Col>
                <Button
                  icon={<FilePdfOutlined />}
                  type="primary"
                  onClick={() =>
                    handleDownloadPdf('Fabrication checklist for this product')
                  }
                >
                  Download PDF
                </Button>
              </Col>
            </Row>

            <Divider />

            <Row gutter={24}>
              <Col span={12}>
                <Title level={5}>External</Title>
                <Table
                  size="small"
                  pagination={false}
                  bordered
                  columns={[
                    { title: 'Inspection Criteria', dataIndex: 'criteria' },
                    { title: 'Pass (✓ / ✗)', dataIndex: 'result', width: 120 },
                  ]}
                  dataSource={[
                    {
                      key: 'front',
                      criteria: 'Front – all bracing straight & secure',
                      result: '✓  (J. Smith)',
                    },
                    {
                      key: 'door',
                      criteria: 'Door handles secure & aligned',
                      result: '✓  (J. Smith)',
                    },
                    {
                      key: 'escape',
                      criteria: 'Escape hatch functional',
                      result: '✓  (L. Mokoena)',
                    },
                  ]}
                  style={{ marginBottom: 16 }}
                />

                <Title level={5}>Roof / Frame</Title>
                <Table
                  size="small"
                  pagination={false}
                  bordered
                  columns={[
                    { title: 'Inspection Criteria', dataIndex: 'criteria' },
                    { title: 'Pass (✓ / ✗)', dataIndex: 'result', width: 120 },
                  ]}
                  dataSource={[
                    {
                      key: 'roof',
                      criteria: 'Roof welds continuous, no defects',
                      result: '✓  (R. Brown)',
                    },
                    {
                      key: 'frame',
                      criteria: 'Frame aligned & square',
                      result: '✓  (R. Brown)',
                    },
                  ]}
                />
              </Col>

              <Col span={12}>
                <Title level={5}>Internal</Title>
                <Table
                  size="small"
                  pagination={false}
                  bordered
                  columns={[
                    { title: 'Inspection Criteria', dataIndex: 'criteria' },
                    { title: 'Pass (✓ / ✗)', dataIndex: 'result', width: 120 },
                  ]}
                  dataSource={[
                    {
                      key: 'seats',
                      criteria: 'Seats secure, no sharp edges',
                      result: '✓  (N. Patel)',
                    },
                    {
                      key: 'cabinets',
                      criteria: 'Cabinets installed, welds clean',
                      result: '✓  (N. Patel)',
                    },
                    {
                      key: 'wiring',
                      criteria: 'Cable penetrations sealed',
                      result: '✓  (N. Patel)',
                    },
                  ]}
                  style={{ marginBottom: 16 }}
                />

                <Title level={5}>Notes & Sign-off</Title>
                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 12,
                    minHeight: 80,
                    marginBottom: 12,
                    fontSize: 13,
                  }}
                >
                  Chamber visually inspected, welds dressed and blended. Unit
                  released for Paint &amp; Blast.
                </div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Inspector</Text>
                    <div style={{ marginTop: 4 }}>John Smith</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                      Signed electronically
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Date</Text>
                    <div style={{ marginTop: 4 }}>2025-05-12</div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* ---------- PAINT MODAL ---------- */}
      <Modal
        visible={activeModal === 'paint'}
        onCancel={closeModal}
        footer={null}
        width={800}
        centered
        destroyOnClose
        title={null}
      >
        {selectedProduct && (
          <div
            style={{
              padding: 24,
              borderRadius: 16,
              background: '#ffffff',
            }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Paint Check List – {selectedProduct.productName}
                </Title>
                <Text type="secondary">
                  Painter: SurfaceCoat Team · Serial {selectedProduct.serialNumber}
                </Text>
              </Col>
              <Col>
                <Button
                  icon={<FilePdfOutlined />}
                  type="primary"
                  onClick={() =>
                    handleDownloadPdf('Paint checklist for this product')
                  }
                >
                  Download PDF
                </Button>
              </Col>
            </Row>

            <Divider />

            <Row gutter={24}>
              <Col span={12}>
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Chamber No.">
                    {selectedProduct.mafNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Chamber Size">
                    {selectedProduct.productName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date">
                    2025-05-20
                  </Descriptions.Item>
                  <Descriptions.Item label="Painter">
                    L. Mokoena
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={12}>
                <Title level={5}>Colour &amp; Finish</Title>
                <Table
                  size="small"
                  pagination={false}
                  bordered
                  columns={[
                    { title: 'Item', dataIndex: 'item' },
                    { title: 'YES', dataIndex: 'yes', width: 70 },
                    { title: 'NO', dataIndex: 'no', width: 70 },
                  ]}
                  dataSource={[
                    {
                      key: 'cce',
                      item: 'Colour correct external',
                      yes: '✓',
                      no: '',
                    },
                    {
                      key: 'cci',
                      item: 'Colour correct internal',
                      yes: '✓',
                      no: '',
                    },
                    {
                      key: 'runs',
                      item: 'Runs',
                      yes: '',
                      no: '✓',
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                />

                <Table
                  size="small"
                  pagination={false}
                  bordered
                  columns={[
                    { title: 'Item', dataIndex: 'item' },
                    { title: 'GOOD', dataIndex: 'good', width: 70 },
                    { title: 'POOR', dataIndex: 'poor', width: 70 },
                  ]}
                  dataSource={[
                    {
                      key: 'finishExt',
                      item: 'Finish external',
                      good: '✓',
                      poor: '',
                    },
                    {
                      key: 'finishInt',
                      item: 'Finish internal',
                      good: '✓',
                      poor: '',
                    },
                    {
                      key: 'coverage',
                      item: 'Paint coverage',
                      good: '✓',
                      poor: '',
                    },
                  ]}
                />
              </Col>
            </Row>

            <Divider />

            <Title level={5}>Notes</Title>
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: 12,
                minHeight: 80,
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              Colour verified against customer standard. No major defects. Unit
              cleared for Materials and Stickers.
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Checked &amp; inspected by</Text>
                <div style={{ marginTop: 4 }}>Lerato Mokoena</div>
              </Col>
              <Col span={12}>
                <Text strong>Date</Text>
                <div style={{ marginTop: 4 }}>2025-05-20</div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* ---------- STICKERS MODAL ---------- */}
      <Modal
        visible={activeModal === 'stickers'}
        onCancel={closeModal}
        footer={null}
        width={600}
        centered
        destroyOnClose
        title={null}
      >
        {selectedProduct && (
          <div
            style={{
              padding: 24,
              borderRadius: 16,
              background: '#ffffff',
            }}
          >
            <Title level={4} style={{ marginBottom: 4 }}>
              Stickers – {selectedProduct.productName}
            </Title>
            <Text type="secondary">
              Serial {selectedProduct.serialNumber} · Printed stickers overview
            </Text>

            <Divider />

            <ul
              style={{
                listStyle: 'none',
                paddingLeft: 0,
                marginBottom: 16,
              }}
            >
              {stickerTypes.map((s) => (
                <li
                  key={s}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    marginBottom: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 13,
                  }}
                >
                  <span>{s}</span>
                  <Tag color="green">Printed</Tag>
                </li>
              ))}
            </ul>

            <Text type="secondary" style={{ fontSize: 12 }}>
              Note: actual sticker PDFs / print jobs can be linked here in the
              future (for print-on-demand or reprint).
            </Text>
          </div>
        )}
      </Modal>

      {/* ---------- BOM MODAL ---------- */}
      <Modal
        visible={activeModal === 'bom'}
        onCancel={closeModal}
        footer={null}
        width={900}
        centered
        destroyOnClose
        title={null}
      >
        {selectedProduct && (
          <div
            style={{
              padding: 24,
              borderRadius: 16,
              background: '#ffffff',
            }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Bill of Materials – {selectedProduct.productName}
                </Title>
                <Text type="secondary">
                  Serial {selectedProduct.serialNumber} · Project BOM snapshot
                </Text>
              </Col>
              <Col>
                <Button
                  icon={<FilePdfOutlined />}
                  type="primary"
                  onClick={() =>
                    handleDownloadPdf('Bill of Materials for this product')
                  }
                >
                  Download BOM (PDF)
                </Button>
              </Col>
            </Row>

            <Divider />

            <Table
              size="small"
              pagination={false}
              bordered
              columns={[
                { title: 'Item No.', dataIndex: 'itemNo', width: 160 },
                { title: 'Description', dataIndex: 'description' },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  width: 100,
                  align: 'right',
                },
                { title: 'Location', dataIndex: 'location', width: 140 },
              ]}
              dataSource={DEMO_BOM}
            />

            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Text strong>Total lines: {DEMO_BOM.length}</Text>
            </div>
          </div>
        )}
      </Modal>
    </SubPageLayout>
  );
};

export default ProductionTimeline;
